import DeleteModal from '../common/DeleteModal'
import Tooltip from '../common/Tooltip'
import { useHistory } from 'react-router-dom'
import { useState, useEffect, useContext } from 'react'
import {
    useFirestoreUsersApproved,
    useFirestoreGeneral,
    useFirestoreGeneralOrderBy,
    useFirestoreGeneralTwoOrderBy,
} from '../../firebase/useFirestore'
import { db } from '../../firebase/config'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { Auth } from '../../StateManagment/Auth'
import { client } from '../../hooks/Client'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import firebase from '../../firebase/compat'
import getTeamMembers from '../../helpers/getTeamMembers'
import { useTaskDTO } from '../../hooks/firebase/TaskDto'
import Table from '../common/table/Table.jsx'
import TableCell from '../common/table/TableCell.jsx'
import TableDragHandleCell from '../common/table/TableDragHandleCell.jsx'
import TableTitleCell from '../common/table/TableTitleCell.jsx'
import softDeleteDoc from '../../helpers/softDeleteDoc';

const TasksModalContent = ({
    type,
    adressID = '',
    personaID = '',
    measureMomentID = '',
    activityFormID = '',
    teamMemberID = '',
    momentTitle = '',
    personaName = '',
    researchID = '',
    questionnaireID = '',
}) => {
    // Context
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // DTO
    const taskDTO = useTaskDTO()

    // State
    const [dragArray, setDragArray] = useState([])
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteName, setDeleteName] = useState('')
    const [deleteDocid, setDeleteDocid] = useState('')
    const [teamMembers, setTeamMembers] = useState([])

    // Firestore
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-name-mismatch
    const tasksAll = useFirestoreGeneralOrderBy(
        'Tasks',
        type === 'activityForm' ? 'ActivityFormID' : 'MeasureMomentID',
        type === 'activityForm' ? activityFormID : measureMomentID,
        'Position',
        'asc'
    )
    const tasksFiltered = useFirestoreGeneralTwoOrderBy(
        'Tasks',
        type === 'activityForm' ? 'ActivityFormID' : 'MeasureMomentID',
        type === 'activityForm' ? activityFormID : measureMomentID,
        'PersonaID',
        personaID,
        'Position',
        'asc'
    )
    const tasks = personaID ? tasksFiltered : tasksAll
    const team = useFirestoreUsersApproved()
    const personas = useFirestoreGeneral('Personas', 'Status', 'active')
    const activityForms = useFirestoreGeneral('EventForms', 'ID', activityFormID || '')
    const measureMoments = useFirestoreGeneral('MeasureMoments', 'ID', measureMomentID || '')
    const resolvedResearchId = researchID || measureMoments?.[0]?.ResearchID || ''
    const researchDocs = useFirestoreGeneral('Research', 'ID', resolvedResearchId)
    const history = useHistory()

    useEffect(() => {
        setDragArray(tasks)
    }, [tasks])

    useEffect(() => {
        getTeamMembers(team, personas, setTeamMembers)
    }, [team, personas])

    // DRAG & DROP
    const handleDragEnd = (result) => {
        if (!result.destination) return

        const items = Array.from(dragArray)
        const [movedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, movedItem)

        setDragArray(items)

        items.forEach((dragItem, idx) => {
            db.collection('Tasks').doc(dragItem.docid).update({ Position: idx + 1 })
        })

        setSaved('flex')
    }

    // ----- Date/Time helpers -----
    const pad2 = (n) => String(n).padStart(2, '0')

    const tsToDateValue = (ts) => {
        if (!ts) return ''
        const d = ts.toDate()
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    }

    const tsToTimeValue = (ts) => {
        if (!ts) return ''
        const d = ts.toDate()
        return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    }

    const buildDateFromInputs = ({ dateStr, timeStr }) => {
        if (!dateStr) return null
        const [y, m, d] = dateStr.split('-').map(Number)
        const [hh, mm] = (timeStr || '00:00').split(':').map(Number)
        return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0)
    }

    const getFallbackDateStr = () => {
        const now = new Date()
        return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
    }

    // Add task
    const addTask = async () => {
        const base = taskDTO.newDoc({
            startDate: null,
            endDate: null,
            userId: auth?.ID || '',
            companyId: client,
            AdressID: adressID || null,
            PersonaID: personaID || null,
            MeasureMomentID: measureMomentID || null,
            ActivityFormID: activityFormID || null,
            TeamMemberID: teamMemberID || null,
            Type: type || 'general',
        })

        const autoTitle = momentTitle && personaName
            ? `${momentTitle} - ${personaName}`
            : momentTitle || 'Nieuwe taak'
        const activityFormTitle = activityForms?.[0]?.Title || ''
        const measureMomentTitle = momentTitle || measureMoments?.[0]?.Title || ''
        const researchTitle = researchDocs?.[0]?.Title || ''
        const autoDescription =
            type === 'activityForm'
                ? activityFormTitle
                : type === 'measureMoment'
                    ? [researchTitle, measureMomentTitle].filter(Boolean).join(' - ')
                    : ''

        await taskDTO.create({
            ...base,
            ActivityFormID: activityFormID || null,
            ResearchID: resolvedResearchId || null,
            QuestionnaireID: questionnaireID || null,
            Title: autoTitle,
            Description: autoDescription,
            Position: tasks?.length ? tasks.length + 1 : 1,
            TeamMembers: [],
            Type: type || 'general',
        })

        setSaved('flex')
    }

    // Title handler
    const titleHandler = async (e) => {
        const docid = e.target.dataset.docid
        const value = e.target.value

        await db.collection('Tasks').doc(docid).update({ Title: value })
        setSaved('flex')
    }

    // Date handlers
    const dateHandler = async (e, task) => {
        const dateStr = e.target.value
        const startTimeStr = tsToTimeValue(task.Timestamp)
        const endTimeStr = tsToTimeValue(task.TimestampEnd)
        const startDate = buildDateFromInputs({ dateStr, timeStr: startTimeStr || '00:00' })
        const endDate = endTimeStr ? buildDateFromInputs({ dateStr, timeStr: endTimeStr }) : null

        await db.collection('Tasks').doc(task.docid).update({
            Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
            TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
        })

        setSaved('flex')
    }

    const startTimeHandler = async (e, task) => {
        const timeStr = e.target.value
        const dateStr = tsToDateValue(task.Timestamp) || tsToDateValue(task.TimestampEnd) || getFallbackDateStr()
        const startDate = buildDateFromInputs({ dateStr, timeStr })

        await db.collection('Tasks').doc(task.docid).update({
            Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
        })

        setSaved('flex')
    }

    const endTimeHandler = async (e, task) => {
        const timeStr = e.target.value

        if (!timeStr) {
            await db.collection('Tasks').doc(task.docid).update({ TimestampEnd: null })
            setSaved('flex')
            return
        }

        const dateStr = tsToDateValue(task.Timestamp) || tsToDateValue(task.TimestampEnd) || getFallbackDateStr()
        const endDate = buildDateFromInputs({ dateStr, timeStr })

        await db.collection('Tasks').doc(task.docid).update({
            TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
        })

        setSaved('flex')
    }

    // Delete modal
    const deleteModalHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const name = e.currentTarget.dataset.deletename

        setDeleteDocid(docid)
        setDeleteName(name)
        setDeleteModal(!deleteModal)
    }

    const deleteTask = () => {
        softDeleteDoc({ collection: 'Tasks', docid: deleteDocid })
            .then(() => setSaved('flex'))
    }

    return (
        <div className='table-container'>
            <Table
                columns={[
                    { key: 'drag', label: '', searchable: false },
                    { key: 'title', label: 'TAAK' },
                    { key: 'date', label: 'DATUM', searchable: false },
                    { key: 'start', label: 'START', searchable: false },
                    { key: 'end', label: 'EIND', searchable: false },
                    { key: 'team', label: 'TEAM', searchable: false },
                    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                ]}
                items={dragArray || []}
                getItemKey={(task) => task.ID}
                renderRow={(task) => (
                    <>
                        <TableDragHandleCell>
                            <DragIndicatorOutlinedIcon />
                        </TableDragHandleCell>
                        <TableTitleCell
                            value={task.Title}
                            to={`/${client}/taskdetail/${task.ID}/${activityFormID}`}
                            onBlur={titleHandler}
                            docid={task.docid}
                            placeholder='Noteer hier de titel'
                        />
                        <TableCell>
                            <input
                                type='date'
                                value={tsToDateValue(task.Timestamp)}
                                onChange={(e) => dateHandler(e, task)}
                            />
                        </TableCell>
                        <TableCell>
                            <input
                                type='time'
                                value={tsToTimeValue(task.Timestamp)}
                                onChange={(e) => startTimeHandler(e, task)}
                            />
                        </TableCell>
                        <TableCell>
                            <input
                                type='time'
                                value={tsToTimeValue(task.TimestampEnd)}
                                onChange={(e) => endTimeHandler(e, task)}
                            />
                        </TableCell>
                        <TableCell>
                            <ul>
                                {task.TeamMembers && task.TeamMembers.map((memberId) => (
                                    <li key={memberId}>
                                        {teamMembers
                                            .filter((m) => m.ID === memberId)
                                            .map((m) => m.Name)
                                            .join(', ')}
                                    </li>
                                ))}
                            </ul>
                        </TableCell>
                        <TableCell>
                            <Tooltip content='Taak verwijderen' top='-60px' width='30px'>
                                <DeleteOutlinedIcon
                                    className='table-delete-icon'
                                    data-docid={task.docid}
                                    data-deletename={task.Title}
                                    onClick={deleteModalHandler}
                                    alt=''
                                />
                            </Tooltip>
                        </TableCell>
                    </>
                )}
                searchAccessor={(task) => task?.Title || ''}
                searchPlaceholder='Zoeken op taak'
                emptyMessage='Geen taken gevonden.'
                draggable
                droppableId='tasks'
                onDragEnd={handleDragEnd}
                addRow={{
                    toolTipContent: 'Taak toevoegen',
                    onClick: addTask,
                }}
            />

            <DeleteModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                deleteItem={deleteTask}
                deleteName={deleteName}
                softDelete
              />
        </div>
    )
}

export default TasksModalContent
